import { useState } from "react";
import { motion } from "framer-motion";
import { siteData, images } from "../data";
import { MapPin, Phone, Mail, Send, Instagram, Facebook } from "lucide-react";

export default function Kontakt() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch("https://api.dev.auxo.dk/webform/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setSubmitted(true);
      }
    } catch (error) {
      console.error("Submission error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white min-h-screen pt-20 font-sans">
      
      {/* Hero Block */}
      <section 
        className="relative h-[40vh] md:h-[50vh] bg-cover bg-center"
        style={{ backgroundImage: `url(${images.subpageHero})` }}
      >
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute inset-0 flex items-center justify-center text-center px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
             <span className="text-primary tracking-widest text-sm uppercase mb-4 block">Vi glæder os til at høre fra dig</span>
             <h1 className="text-4xl md:text-6xl text-white font-serif tracking-wide text-shadow-sm">Kontakt Os</h1>
          </motion.div>
        </div>
      </section>

      {/* Content Block */}
      <section className="py-16 md:py-24 bg-white text-gray-800">
         <div className="container mx-auto px-6 max-w-6xl">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-24 items-start">
              
              {/* Contact Info */}
              <div>
                <h2 className="text-3xl font-serif mb-8">Lad os tage en snak</h2>
                <p className="text-gray-600 text-lg mb-12 leading-relaxed">
                  Vi står altid klar til at høre fra dig. Uanset om du vil bestille bord, forespørge på catering eller planlægge et større arrangement, er du velkommen til at kontakte os.
                </p>

                <div className="space-y-8 mb-16">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-bg-light flex items-center justify-center rounded-none shadow-sm shrink-0">
                      <MapPin className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-serif text-xl mb-1">Besøg os</h3>
                      <p className="text-gray-600">{siteData.address}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-bg-light flex items-center justify-center rounded-none shadow-sm shrink-0">
                      <Phone className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-serif text-xl mb-1">Ring</h3>
                      <a href={`tel:${siteData.phone.replace(/\s+/g, '')}`} className="text-gray-600 hover:text-primary transition-colors">{siteData.phone}</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-bg-light flex items-center justify-center rounded-none shadow-sm shrink-0">
                      <Mail className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-serif text-xl mb-1">Email</h3>
                      <a href={`mailto:${siteData.email}`} className="text-gray-600 hover:text-primary transition-colors">{siteData.email}</a>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 pt-4">
                    <a
                      href={siteData.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 bg-bg-light hover:bg-primary hover:text-white transition-all text-sm font-medium rounded-none"
                    >
                      <Instagram className="w-4 h-4" />
                      Følg os på Instagram
                    </a>
                    <a
                      href={siteData.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 bg-bg-light hover:bg-primary hover:text-white transition-all text-sm font-medium rounded-none"
                    >
                      <Facebook className="w-4 h-4" />
                      Facebook
                    </a>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-bg-light p-[30px] shadow-sm">
                {submitted ? (
                  <div className="text-center py-20">
                    <div className="w-20 h-20 bg-white text-primary rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm">
                      <Send className="w-10 h-10" />
                    </div>
                    <h2 className="text-3xl font-serif mb-4">Tak for din besked</h2>
                    <p className="text-gray-500">Vi vender tilbage til dig hurtigst muligt.</p>
                    <button 
                      onClick={() => setSubmitted(false)}
                      className="mt-8 text-primary uppercase text-sm tracking-widest border-b border-primary/40 hover:border-primary transition-colors pb-1"
                    >
                      Send en ny besked
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Honeypot */}
                    <div className="sr-only absolute -top-[9999px] -left-[9999px]">
                      <label htmlFor="website_url">Website URL</label>
                      <input type="text" name="website_url" id="website_url" tabIndex="-1" autoComplete="off" />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="text-sm tracking-widest uppercase text-dark">Navn</label>
                        <input 
                          required
                          type="text" 
                          name="name"
                          className="w-full px-4 py-3 bg-white border-transparent focus:border-primary outline-none transition-all"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm tracking-widest uppercase text-dark">Email</label>
                        <input 
                          required
                          type="email" 
                          name="email"
                          className="w-full px-4 py-3 bg-white border-transparent focus:border-primary outline-none transition-all"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm tracking-widest uppercase text-dark">Emne</label>
                      <input 
                        required
                        type="text" 
                        name="subject"
                        className="w-full px-4 py-3 bg-white border-transparent focus:border-primary outline-none transition-all"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm tracking-widest uppercase text-dark">Besked</label>
                      <textarea 
                        required
                        name="message"
                        rows="6"
                        className="w-full px-4 py-3 bg-white border-transparent focus:border-primary outline-none transition-all resize-none"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-4 bg-primary text-white hover:bg-dark transition-all duration-300 tracking-widest text-sm uppercase mt-4 disabled:opacity-50"
                    >
                      {loading ? "Sender..." : "Send Besked"}
                    </button>
                  </form>
                )}
              </div>
            </div>
         </div>
      </section>

    </div>
  );
}
